import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Drawer,
  Empty,
  List,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Steps,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  ThunderboltOutlined,
  CheckOutlined,
  PushpinOutlined,
  PushpinFilled,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  supabase,
  useSupabaseSession,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useFleetOperator,
  formatDate,
  minutesToTimeString,
  planDailyScheduleForDate,
  publishSchedulePlan,
  assignJobToVehicle,
  unassignJobFromPlan,
  enrichDuplicateAssignments,
  actionableDuplicateJobs,
  jobsMissingOrgForHqPublish,
  applyJobOrgToPlan,
  suggestVehiclesForJob,
  draftStorageKey,
  serializeDailyScheduleDraft,
  parseDailyScheduleDraft,
  pinJobOnPlan,
  unpinJobOnPlan,
  usePortalPermissions,
  isSchedulePublishChainOk,
  listScheduleFixReport,
  isPastSchedulePickup,
  countPublishableJobs,
  type DailySchedulePlan,
  type DailyScheduleDraft,
  type VehicleSuggestion,
} from '@ubus/shared';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import AdminSectionPage from '../components/AdminSectionPage';
import VehicleScheduleTimeline from '../components/VehicleScheduleTimeline';
import ForbiddenPage from './ForbiddenPage';
import { translatePublishError, translateScheduleIssues } from '../i18n/translateErrors';
import ScheduleFixPanel from '../components/ScheduleFixPanel';
import { localeConfirm } from '../utils/localeModal';

const { Text } = Typography;

interface OrgOption {
  id: string;
  name: string;
}

interface FleetVehicleMeta {
  id: string;
  fleet_operator_id: string | null;
  preferred_driver_id: string | null;
}

interface FleetDriverOption {
  id: string;
  name: string;
  fleet_operator_id: string | null;
}

/** Prefill preferred driver only when driverId is still unset. */
function seedPreferredDrivers(
  plan: DailySchedulePlan,
  preferredByVehicle: Map<string, string | null>
): DailySchedulePlan {
  return {
    ...plan,
    vehicles: plan.vehicles.map((v) => {
      if (v.driverId !== undefined) return v;
      const preferred = preferredByVehicle.get(v.vehicleId);
      return preferred ? { ...v, driverId: preferred } : v;
    }),
  };
}

function minutesLabel(minutes: number): string {
  return minutesToTimeString(minutes).slice(0, 5);
}

function parseDateParam(raw: string | null): Dayjs {
  if (raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
    return dayjs(raw, 'YYYY-MM-DD');
  }
  // Ops default: plan the next calendar day (e.g. open on 23rd → 24th)
  return dayjs().add(1, 'day').startOf('day');
}

type VehicleSelectOption = {
  label: string;
  options: Array<{ value: string; label: string; disabled: boolean }>;
};

function suggestionOptionLabel(s: VehicleSuggestion, t: TFunction): string {
  const fleet = s.fleetNumber ?? s.vehicleId.slice(0, 8);
  if (s.feasible) {
    const km = (s.deadheadKm ?? 0).toFixed(1);
    return t('schedule.suggestionFeasible', { fleet, km });
  }
  const reason = translateScheduleIssues(s.issues.slice(0, 1), t);
  return t('schedule.suggestionInfeasible', { fleet, reason });
}

function vehicleSuggestionOptions(
  plan: DailySchedulePlan,
  jobId: string,
  t: TFunction,
  excludeVehicleId?: string
): VehicleSelectOption[] {
  const suggestions = suggestVehiclesForJob(plan, jobId).filter(
    (s) => s.vehicleId !== excludeVehicleId
  );
  const feasible = suggestions.filter((s) => s.feasible);
  const infeasible = suggestions.filter((s) => !s.feasible);
  const groups: VehicleSelectOption[] = [];
  if (feasible.length > 0) {
    groups.push({
      label: t('schedule.suggestedVehicles'),
      options: feasible.map((s) => ({
        value: s.vehicleId,
        label: suggestionOptionLabel(s, t),
        disabled: false,
      })),
    });
  }
  if (infeasible.length > 0) {
    groups.push({
      label: t('schedule.otherVehicles'),
      options: infeasible.map((s) => ({
        value: s.vehicleId,
        label: suggestionOptionLabel(s, t),
        disabled: true,
      })),
    });
  }
  return groups;
}

function jobIdsOnVehicle(plan: DailySchedulePlan, vehicleId: string): string[] {
  const legs = plan.vehicles.find((v) => v.vehicleId === vehicleId)?.legs ?? [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const leg of legs) {
    if (!seen.has(leg.jobId)) {
      seen.add(leg.jobId);
      ids.push(leg.jobId);
    }
  }
  return ids;
}

function isFixedJobOnVehicle(plan: DailySchedulePlan, vehicleId: string, jobId: string): boolean {
  const legs = plan.vehicles.find((v) => v.vehicleId === vehicleId)?.legs ?? [];
  return legs.some((l) => l.jobId === jobId && l.isFixed);
}

function publishedBlockCount(plan: DailySchedulePlan): number {
  return plan.vehicles.reduce((n, v) => n + v.legs.filter((l) => l.isFixed).length, 0);
}

function DailySchedule() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  void i18n.language;
  const { session, loading: authLoading } = useSupabaseSession();
  const user = session?.user ?? null;
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const isFleetOwner = isFleetOwnerRole(parseUserRole(user));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const canAccessPage = isHqAdmin || (isFleetOwner && Boolean(operatorId));
  const { hasPermission } = usePortalPermissions();

  const canGenerate = hasPermission('daily_schedule.generate');
  const canPublishPlan = hasPermission('daily_schedule.publish');
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState<Dayjs>(() => parseDateParam(searchParams.get('date')));
  const [plan, setPlan] = useState<DailySchedulePlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  /** Pending vehicle pick per job (unassigned assign / reassign). */
  const [vehiclePick, setVehiclePick] = useState<Record<string, string>>({});
  const [customerDrawerJobId, setCustomerDrawerJobId] = useState<string | null>(null);
  const [customerOrgId, setCustomerOrgId] = useState<string | undefined>();
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [draftBanner, setDraftBanner] = useState<DailyScheduleDraft | null>(null);
  /** Timeline click-to-assign: selected unassigned job id. */
  const [assignJobId, setAssignJobId] = useState<string | null>(null);
  const [highlightJobId, setHighlightJobId] = useState<string | null>(null);
  const [flashVehicleId, setFlashVehicleId] = useState<string | null>(null);
  /** CONF-20260719 W3: assignment list collapsed by default; gantt + unassigned are primary. */
  const [assignmentsOpenKeys, setAssignmentsOpenKeys] = useState<string[]>([]);
  const [fleetVehicles, setFleetVehicles] = useState<FleetVehicleMeta[]>([]);
  const [fleetDrivers, setFleetDrivers] = useState<FleetDriverOption[]>([]);

  const scheduleDate = date.format('YYYY-MM-DD');
  const tripsListPath = isHqAdmin
    ? `/trips?date=${scheduleDate}`
    : `/trips?date=${scheduleDate}`;
  const userId = user?.id ?? '';

  const preferredByVehicle = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const v of fleetVehicles) {
      map.set(v.id, v.preferred_driver_id);
    }
    return map;
  }, [fleetVehicles]);

  const operatorByVehicle = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const v of fleetVehicles) {
      map.set(v.id, v.fleet_operator_id);
    }
    return map;
  }, [fleetVehicles]);

  const applyPlan = useCallback(
    (next: DailySchedulePlan) => {
      setPlan(seedPreferredDrivers(next, preferredByVehicle));
    },
    [preferredByVehicle]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [vehiclesRes, driversRes] = await Promise.all([
        supabase
          .from('vehicles')
          .select('id, fleet_operator_id, preferred_driver_id')
          .eq('status', 'active'),
        supabase
          .from('drivers')
          .select('id, name, fleet_operator_id')
          .eq('status', 'active')
          .order('name'),
      ]);
      if (cancelled) return;
      if (vehiclesRes.data) setFleetVehicles(vehiclesRes.data as FleetVehicleMeta[]);
      if (driversRes.data) setFleetDrivers(driversRes.data as FleetDriverOption[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (preferredByVehicle.size === 0) return;
    setPlan((prev) => (prev ? seedPreferredDrivers(prev, preferredByVehicle) : prev));
  }, [preferredByVehicle]);

  const clearDraftStorage = useCallback(() => {
    if (!userId) return;
    sessionStorage.removeItem(draftStorageKey(userId, scheduleDate));
  }, [userId, scheduleDate]);

  const duplicateDetails = useMemo(
    () => (plan ? enrichDuplicateAssignments(plan) : []),
    [plan]
  );
  const plannerDuplicates = useMemo(
    () => (plan ? actionableDuplicateJobs(plan) : []),
    [plan]
  );
  const publishedSplits = useMemo(
    () => duplicateDetails.filter((d) => d.kind === 'published_split'),
    [duplicateDetails]
  );
  const missingOrgJobs = useMemo(
    () => (plan ? jobsMissingOrgForHqPublish(plan) : []),
    [plan]
  );

  useEffect(() => {
    const fromUrl = searchParams.get('date');
    if (!fromUrl) {
      setSearchParams({ date: scheduleDate }, { replace: true });
      return;
    }
    if (dayjs(fromUrl, 'YYYY-MM-DD', true).isValid() && fromUrl !== scheduleDate) {
      setPlan(null);
      setVehiclePick({});
      setDraftBanner(null);
      setCustomerDrawerJobId(null);
      setCustomerOrgId(undefined);
      setAssignJobId(null);
      setHighlightJobId(null);
      setFlashVehicleId(null);
      setDate(dayjs(fromUrl, 'YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → state only when searchParams change
  }, [searchParams]);

  const setDateAndUrl = (d: Dayjs) => {
    setPlan(null);
    setVehiclePick({});
    setDraftBanner(null);
    setCustomerDrawerJobId(null);
    setCustomerOrgId(undefined);
    setAssignJobId(null);
    setHighlightJobId(null);
    setFlashVehicleId(null);
    setDate(d);
    setSearchParams({ date: d.format('YYYY-MM-DD') }, { replace: true });
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openAssignmentsAndScroll = () => {
    setAssignmentsOpenKeys(['assignments']);
    requestAnimationFrame(() => scrollToSection('schedule-assignments'));
  };
  const jobsById = useMemo(() => {
    const map = new Map((plan?.jobs ?? []).map((j) => [j.id, j]));
    return map;
  }, [plan?.jobs]);

  const runGenerate = useCallback(async () => {
    setLoading(true);
    try {
      const pins = plan?.pins ?? {};
      const result = await planDailyScheduleForDate(scheduleDate, {
        pinnedAssignments: Object.keys(pins).length > 0 ? pins : undefined,
      });
      applyPlan(result);
      setVehiclePick({});
      setDraftBanner(null);
      setAssignJobId(null);
      setHighlightJobId(null);
      const dir = result.directionsPrefetch;
      if (dir && dir.requestedPairs > 0) {
        if (dir.hadError || dir.rateLimited || dir.resolvedPairs < dir.requestedPairs) {
          message.warning(
            t('schedule.directionsFallbackWarning', {
              resolved: dir.resolvedPairs,
              requested: dir.requestedPairs,
              cacheHits: dir.cacheHits,
              fetched: dir.fetchedPairs,
            })
          );
        } else if (dir.cacheHits > 0 || dir.fetchedPairs > 0) {
          message.info(
            t('schedule.directionsPrefetchOk', {
              cacheHits: dir.cacheHits,
              fetched: dir.fetchedPairs,
            })
          );
        }
      }
      if (result.pinFailures && result.pinFailures.length > 0) {
        message.warning(
          t('schedule.pinFailuresWarning', {
            count: result.pinFailures.length,
            jobs: result.pinFailures.map((f) => f.title).join(', '),
          })
        );
      }
      if (result.unassignedJobIds.length > 0) {
        message.warning(t('schedule.unassignedWarning', { count: result.unassignedJobIds.length }));
      } else if (!result.pinFailures?.length) {
        const published = publishedBlockCount(result);
        message.success(
          t('schedule.summary', {
            vehicles: result.metrics.vehicleCount,
            jobs: result.metrics.assignedJobCount,
            deadhead: result.metrics.totalDeadheadKm.toFixed(1),
            published: published > 0 ? t('schedule.summaryPublished', { count: published }) : '',
            unassigned:
              result.unassignedJobIds.length > 0
                ? t('schedule.summaryUnassigned', { count: result.unassignedJobIds.length })
                : '',
          })
        );
      }
    } catch (err) {
      console.error(err);
      message.error(err instanceof Error ? err.message : t('errors.generatePlanFailed'));
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [plan?.pins, scheduleDate, t, applyPlan]);

  const generatePlan = useCallback(() => {
    if (plan) {
      const pinCount = Object.keys(plan.pins ?? {}).length;
      localeConfirm(t, {
        title: t('schedule.replacePlanTitle'),
        content:
          pinCount > 0
            ? t('schedule.replacePlanContentWithPins', { count: pinCount })
            : t('schedule.replacePlanContent'),
        okText: t('schedule.generate'),
        onOk: () => runGenerate(),
      });
      return;
    }
    void runGenerate();
  }, [plan, runGenerate, t]);

  useEffect(() => {
    if (plan) {
      setDraftBanner(null);
      return;
    }
    if (!userId) {
      setDraftBanner(null);
      return;
    }
    const raw = sessionStorage.getItem(draftStorageKey(userId, scheduleDate));
    const parsed = raw ? parseDailyScheduleDraft(raw) : null;
    setDraftBanner(parsed && parsed.scheduleDate === scheduleDate ? parsed : null);
  }, [scheduleDate, userId, plan]);

  useEffect(() => {
    if (!plan || !userId) return;
    sessionStorage.setItem(
      draftStorageKey(userId, scheduleDate),
      serializeDailyScheduleDraft({
        scheduleDate,
        savedAt: new Date().toISOString(),
        plan,
      })
    );
  }, [plan, userId, scheduleDate]);

  useEffect(() => {
    if (!customerDrawerJobId) return;
    void supabase
      .from('organizations')
      .select('id, name')
      .order('name')
      .then(({ data }) => setOrgOptions((data as OrgOption[]) ?? []));
  }, [customerDrawerJobId]);

  const openCustomerDrawer = (jobId: string) => {
    setCustomerDrawerJobId(jobId);
    setCustomerOrgId(undefined);
  };

  const closeCustomerDrawer = () => {
    setCustomerDrawerJobId(null);
    setCustomerOrgId(undefined);
  };

  const saveCustomerOnPlan = async () => {
    if (!plan || !customerDrawerJobId) return;
    if (!customerOrgId) {
      message.warning(t('schedule.addCustomerRequired'));
      return;
    }
    setSavingCustomer(true);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ org_id: customerOrgId, updated_at: new Date().toISOString() })
        .eq('id', customerDrawerJobId);
      if (error) throw error;

      const patched = applyJobOrgToPlan(plan, customerDrawerJobId, customerOrgId);
      if (!patched.ok) {
        message.error(t('schedule.addCustomerFailed'));
        return;
      }
      applyPlan(patched.plan);
      message.success(t('schedule.addCustomerSuccess'));

      const stillMissing = jobsMissingOrgForHqPublish(patched.plan);
      if (stillMissing.length > 0) {
        openCustomerDrawer(stillMissing[0].id);
      } else {
        closeCustomerDrawer();
      }
    } catch (err) {
      console.error(err);
      message.error(err instanceof Error ? err.message : t('schedule.addCustomerFailed'));
    } finally {
      setSavingCustomer(false);
    }
  };

  const applyAssignToVehicle = (jobId: string, vehicleId: string) => {
    if (!plan) return;
    const result = assignJobToVehicle(plan, jobId, vehicleId);
    if (!result.ok) {
      message.error(translateScheduleIssues(result.issues, t) || result.errors.join('; '));
      setFlashVehicleId(vehicleId);
      window.setTimeout(() => setFlashVehicleId(null), 1200);
      return;
    }
    applyPlan(result.plan);
    setVehiclePick((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    setAssignJobId(null);
    setHighlightJobId(jobId);
    const wasAssigned = plan.vehicles.some((v) =>
      v.legs.some((l) => l.jobId === jobId)
    );
    message.success(wasAssigned ? t('schedule.jobReassigned') : t('schedule.jobAssigned'));
  };

  const handleAssign = (jobId: string) => {
    if (!plan) return;
    const vehicleId = vehiclePick[jobId];
    if (!vehicleId) {
      message.warning(t('schedule.selectVehicleFirst'));
      return;
    }
    applyAssignToVehicle(jobId, vehicleId);
  };

  const handleReassign = (jobId: string) => {
    if (!plan) return;
    const vehicleId = vehiclePick[`re:${jobId}`];
    if (!vehicleId) {
      message.warning(t('schedule.selectTargetVehicleFirst'));
      return;
    }
    const result = assignJobToVehicle(plan, jobId, vehicleId);
    if (!result.ok) {
      message.error(translateScheduleIssues(result.issues, t) || result.errors.join('; '));
      return;
    }
    applyPlan(result.plan);
    setVehiclePick((prev) => {
      const next = { ...prev };
      delete next[`re:${jobId}`];
      return next;
    });
    message.success(t('schedule.jobReassigned'));
  };

  const handleUnassign = (jobId: string) => {
    if (!plan) return;
    applyPlan(unassignJobFromPlan(plan, jobId));
    message.info(t('schedule.jobMovedToUnassigned'));
  };

  const legRows = useMemo(() => {
    if (!plan) return [];
    const fleetById = new Map((plan.fleet ?? []).map((f) => [f.id, f]));
    return plan.vehicles.flatMap((v) => {
      const capacity = fleetById.get(v.vehicleId)?.capacity ?? null;
      return v.validation.timeline.map((entry) => ({
        key: `${v.vehicleId}-${entry.legId}`,
        vehicle: v.fleetNumber ?? v.vehicleId.slice(0, 8),
        capacity,
        passengers: jobsById.get(entry.jobId)?.passengerCount ?? null,
        label: entry.label ?? entry.legId,
        jobId: entry.jobId,
        arrive: minutesLabel(entry.arriveMinutes),
        depart: minutesLabel(entry.departMinutes),
        expired: isPastSchedulePickup(scheduleDate, entry.arriveMinutes),
      }));
    });
  }, [plan, jobsById, scheduleDate]);

  // Must stay above auth early-returns — conditional hooks → React #310.
  const emphasizeVehicleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const dup of publishedSplits) {
      for (const v of dup.vehicles) ids.add(v.vehicleId);
    }
    return ids;
  }, [publishedSplits]);

  if (authLoading || fleetLoading) {
    return (
      <AdminSectionPage
        title={t('schedule.title')}
        subtitle={t('schedule.subtitle')}
        widthMode="canvas"
        loading
      />
    );
  }

  if (!canAccessPage) {
    return <ForbiddenPage />;
  }

  const formatPublishScheduleError = (err: unknown): string => {
    if (!(err instanceof Error)) return t('errors.publishFailed');
    return translatePublishError(err.message, t, missingOrgJobs);
  };

  const confirmPublish = () => {
    if (!plan) return;
    if (!isSchedulePublishChainOk(plan)) {
      message.error(t('schedule.cannotPublishInfeasible'));
      return;
    }
    const publishableCount = countPublishableJobs(plan);
    if (publishableCount === 0) {
      message.error(t('schedule.nothingToPublish'));
      return;
    }
    if (actionableDuplicateJobs(plan).length > 0) {
      message.error(t('schedule.consolidateDuplicates'));
      return;
    }
    if (missingOrgJobs.length > 0) {
      message.error(
        t('schedule.missingCustomerPublish', {
          count: missingOrgJobs.length,
          jobs: missingOrgJobs.map((j) => j.title).join(', '),
        })
      );
      return;
    }

    const unassignedCount = plan.unassignedJobIds.length;
    localeConfirm(t, {
      title: t('schedule.publishTitle'),
      content:
        unassignedCount > 0
          ? t('schedule.publishContentPartial', {
              date: scheduleDate,
              assigned: publishableCount,
              unassigned: unassignedCount,
            })
          : t('schedule.publishContent', { date: scheduleDate }),
      okText: t('schedule.confirmPublish'),
      onOk: async () => {
        setPublishing(true);
        try {
          const result = await publishSchedulePlan(scheduleDate, plan);
          clearDraftStorage();
          setPlan(null);
          setVehiclePick({});
          setDraftBanner(null);
          setAssignJobId(null);
          localeConfirm(t, {
            title: t('schedule.publishedSuccess', {
              trips: result.trips_created,
              jobs: result.jobs_scheduled,
            }),
            content:
              unassignedCount > 0
                ? t('schedule.publishNextStepPartial', { unassigned: unassignedCount })
                : t('schedule.publishNextStep'),
            okText: t('schedule.openTrips'),
            cancelText: t('schedule.stayOnSchedule'),
            onOk: () => {
              navigate(tripsListPath);
            },
          });
        } catch (err) {
          message.error(formatPublishScheduleError(err));
        } finally {
          setPublishing(false);
        }
      },
    });
  };

  const columns: TableProps<(typeof legRows)[0]>['columns'] = [
    { title: t('schedule.tableVehicle'), dataIndex: 'vehicle', key: 'vehicle', width: 100 },
    {
      title: t('schedule.tableLeg'),
      dataIndex: 'label',
      key: 'label',
      ellipsis: true,
      render: (label: string, row) => (
        <Space size={6} wrap>
          <span>{label}</span>
          {row.expired && <Tag color="default">{t('schedule.expired')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('schedule.tableJob'),
      dataIndex: 'jobId',
      key: 'jobId',
      ellipsis: true,
      render: (id: string) => {
        const title = jobsById.get(id)?.title;
        return title ? (
          <Text>{title}</Text>
        ) : (
          <Text type="secondary" code>
            {id.slice(0, 8)}
          </Text>
        );
      },
    },
    {
      title: t('schedule.tablePassengers'),
      dataIndex: 'passengers',
      key: 'passengers',
      width: 96,
      render: (v: number | null) => (v != null ? v : '—'),
    },
    {
      title: t('schedule.tableCapacity'),
      dataIndex: 'capacity',
      key: 'capacity',
      width: 96,
      render: (v: number | null) => (v != null ? v : '—'),
    },
    { title: t('schedule.tableArrive'), dataIndex: 'arrive', key: 'arrive', width: 80 },
    { title: t('schedule.tableDepart'), dataIndex: 'depart', key: 'depart', width: 80 },
  ];

  const publishedOnlySoftEmpty =
    !!plan &&
    countPublishableJobs(plan) === 0 &&
    publishedBlockCount(plan) > 0 &&
    plannerDuplicates.length === 0;

  const fixReport = plan ? listScheduleFixReport(plan) : { blocking: [], publishedWarnings: [] };
  const chainOkForPublish = plan ? isSchedulePublishChainOk(plan) : false;
  const publishableCount = plan ? countPublishableJobs(plan) : 0;

  const canPublish =
    !!plan &&
    publishableCount > 0 &&
    chainOkForPublish &&
    plannerDuplicates.length === 0 &&
    missingOrgJobs.length === 0;

  const stickyStatus = !plan
    ? t('schedule.statusGenerate')
    : plannerDuplicates.length > 0
      ? t('schedule.statusDraftConflicts', { count: plannerDuplicates.length })
      : fixReport.blocking.length > 0
        ? t('schedule.statusInfeasible')
        : missingOrgJobs.length > 0
          ? t('schedule.statusMissingCustomer', { count: missingOrgJobs.length })
          : publishableCount === 0 && plan.unassignedJobIds.length > 0
            ? t('schedule.statusUnassigned', { count: plan.unassignedJobIds.length })
            : publishedOnlySoftEmpty
              ? t('schedule.statusPublishedOnly')
              : publishableCount === 0
                ? t('schedule.statusGenerate')
                : fixReport.publishedWarnings.length > 0
                  ? t('schedule.statusPublishedWarn', { count: fixReport.publishedWarnings.length })
                  : plan.unassignedJobIds.length > 0
                    ? t('schedule.statusReadyPartial', {
                        assigned: publishableCount,
                        unassigned: plan.unassignedJobIds.length,
                      })
                    : t('schedule.statusReady');

  return (
    <AdminSectionPage
      title={t('schedule.title')}
      subtitle={t('schedule.subtitle')}
      widthMode="canvas"
      bare
      extra={
        <Space size={8} wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/schedule-overview')}
          >
            {t('schedule.backToOverview')}
          </Button>
          <DatePicker
            value={date}
            onChange={(d) => d && setDateAndUrl(d)}
            allowClear={false}
            aria-label={t('schedule.dateLabel')}
          />
          <Button
            type={plan ? 'default' : 'primary'}
            icon={<ThunderboltOutlined />}
            onClick={generatePlan}
            loading={loading}
            disabled={!canGenerate}
          >
            {plan ? t('schedule.regenerate') : t('schedule.generateRecommendation')}
          </Button>
        </Space>
      }
    >
      <div className="admin-stack admin-stack--ops admin-daily-schedule">
        <div className="admin-live-region" aria-live="polite" aria-atomic="true">
          {plan
            ? t('schedule.livePlan', {
                status: stickyStatus,
                vehicles: plan.metrics.vehicleCount,
                jobs: plan.metrics.assignedJobCount,
              })
            : t('schedule.noPlanLoaded')}
        </div>

        {draftBanner && !plan && (
          <Alert
            type="info"
            showIcon
            message={t('schedule.draftFoundTitle')}
            description={t('schedule.draftFoundDescription', {
              time: dayjs(draftBanner.savedAt).format('HH:mm'),
            })}
            action={
              <Space>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    applyPlan(draftBanner.plan);
                    setVehiclePick({});
                    setDraftBanner(null);
                    message.success(t('schedule.draftRestored'));
                  }}
                >
                  {t('schedule.draftRestore')}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    clearDraftStorage();
                    setDraftBanner(null);
                    message.info(t('schedule.draftDiscarded'));
                  }}
                >
                  {t('schedule.draftDiscard')}
                </Button>
              </Space>
            }
          />
        )}

        <Steps
          size="small"
          current={
            !plan
              ? 0
              : fixReport.blocking.length > 0 ||
                  plannerDuplicates.length > 0 ||
                  missingOrgJobs.length > 0 ||
                  publishableCount === 0
                ? 1
                : 2
          }
          items={[
            { title: t('schedule.stepGenerate'), description: t('schedule.stepGenerateDescription') },
            { title: t('schedule.stepResolve'), description: t('schedule.stepResolveDescription') },
            { title: t('schedule.stepPublish'), description: t('schedule.stepPublishDescription') },
          ]}
        />

        <Collapse
          size="small"
          ghost
          items={[
            {
              key: 'howto',
              label: t('schedule.howToTitle'),
              children: <Text type="secondary">{t('schedule.howToDescription')}</Text>,
            },
          ]}
        />

        {!plan && !loading && (
          <Card className="admin-inner-card">
            <Empty description={t('schedule.noPlan')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
              <Button type="primary" icon={<ThunderboltOutlined />} onClick={generatePlan}>
                {t('schedule.generateRecommendation')}
              </Button>
            </Empty>
          </Card>
        )}

        {plan && (
          <>
            <div className="admin-status-strip" role="navigation" aria-label={t('schedule.statusStripLabel')}>
              {plan.unassignedJobIds.length > 0 && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--warn"
                  onClick={() => scrollToSection('schedule-unassigned')}
                >
                  {t('schedule.stripUnassigned', { count: plan.unassignedJobIds.length })}
                </button>
              )}
              {missingOrgJobs.length > 0 && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--error"
                  onClick={() => scrollToSection('schedule-customer')}
                >
                  {t('schedule.stripMissingCustomer', { count: missingOrgJobs.length })}
                </button>
              )}
              {plannerDuplicates.length > 0 && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--error"
                  onClick={() => scrollToSection('schedule-duplicates')}
                >
                  {t('schedule.stripDuplicates', { count: plannerDuplicates.length })}
                </button>
              )}
              {fixReport.blocking.length > 0 && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--error"
                  onClick={() => scrollToSection('schedule-fix-panel')}
                >
                  {t('schedule.stripInfeasible')}
                </button>
              )}
              {fixReport.blocking.length === 0 && fixReport.publishedWarnings.length > 0 && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--warn"
                  onClick={() => scrollToSection('schedule-fix-panel')}
                >
                  {t('schedule.stripPublishedWarn', { count: fixReport.publishedWarnings.length })}
                </button>
              )}
              {canPublish && (
                <button
                  type="button"
                  className="admin-status-strip__chip admin-status-strip__chip--ok"
                  onClick={() => scrollToSection('schedule-publish')}
                >
                  {t('schedule.stripReady')}
                </button>
              )}
            </div>

            {assignJobId && (
              <Alert
                type="info"
                showIcon
                role="status"
                message={t('schedule.assignModeTitle', {
                  job:
                    plan.unassignedJobs.find((j) => j.id === assignJobId)?.title ??
                    jobsById.get(assignJobId)?.title ??
                    assignJobId.slice(0, 8),
                })}
                action={
                  <Button size="small" onClick={() => setAssignJobId(null)}>
                    {t('common.cancel')}
                  </Button>
                }
              />
            )}

            <ScheduleFixPanel
              plan={plan}
              t={t}
              onSelectVehicle={(vehicleId) => {
                setFlashVehicleId(vehicleId);
                window.setTimeout(() => setFlashVehicleId(null), 1600);
                scrollToSection('schedule-timeline');
              }}
              onQuickReassign={(jobId, vehicleId) => {
                applyAssignToVehicle(jobId, vehicleId);
              }}
              onUnassign={handleUnassign}
              onOpenPublishedTrips={() => {
                navigate(tripsListPath);
              }}
            />

            {plan.pinFailures && plan.pinFailures.length > 0 && (
              <Alert
                type="warning"
                showIcon
                role="alert"
                message={t('schedule.pinFailuresTitle')}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {plan.pinFailures.map((f) => (
                      <li key={f.jobId}>
                        <Text strong>{f.title}</Text>
                        {' — '}
                        {translateScheduleIssues(f.issues, t) || t('schedule.pinFailureFallback')}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}

            <Row gutter={[16, 16]} className="admin-stat-grid">
              <Col xs={12} sm={12} md={6}>
                <Card className="admin-inner-card admin-inner-card--compact">
                  <Statistic title={t('schedule.vehiclesUsed')} value={plan.metrics.vehicleCount} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card className="admin-inner-card admin-inner-card--compact">
                  <Statistic title={t('schedule.jobsAssigned')} value={plan.metrics.assignedJobCount} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card className="admin-inner-card admin-inner-card--compact">
                  <Statistic title={t('schedule.publishedBlocks')} value={publishedBlockCount(plan)} />
                </Card>
              </Col>
              <Col xs={12} sm={12} md={6}>
                <Card className="admin-inner-card admin-inner-card--compact">
                  <Statistic
                    title={t('schedule.feasibility')}
                    value={
                      fixReport.blocking.length > 0
                        ? t('schedule.infeasible')
                        : fixReport.publishedWarnings.length > 0
                          ? t('schedule.feasibleWithWarn')
                          : t('schedule.feasible')
                    }
                    valueStyle={{
                      color:
                        fixReport.blocking.length > 0
                          ? 'var(--color-destructive)'
                          : 'var(--color-accent)',
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {publishedOnlySoftEmpty && (
              <Alert
                type="info"
                showIcon
                role="status"
                message={t('schedule.noNewJobsTitle')}
                description={t('schedule.noNewJobsDescription')}
                action={
                  <Space>
                    <Button size="small" onClick={() => navigate(`/jobs?date=${scheduleDate}`)}>
                      {t('schedule.goToJobs')}
                    </Button>
                    <Button size="small" onClick={() => navigate(tripsListPath)}>
                      {t('schedule.reviewTrips')}
                    </Button>
                  </Space>
                }
              />
            )}

            {missingOrgJobs.length > 0 && (
              <Alert
                id="schedule-customer"
                type="error"
                showIcon
                role="alert"
                message={t('schedule.customerRequiredTitle')}
                description={
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text>{t('schedule.customerRequiredDescription')}</Text>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {missingOrgJobs.map((j) => (
                        <li key={j.id}>
                          <Space wrap size={8}>
                            <Text strong>{j.title}</Text>
                            <Button
                              type="link"
                              size="small"
                              style={{ paddingInline: 0 }}
                              onClick={() => openCustomerDrawer(j.id)}
                            >
                              {t('schedule.addCustomer')}
                            </Button>
                          </Space>
                        </li>
                      ))}
                    </ul>
                  </Space>
                }
                action={
                  <Space direction="vertical" size={4}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => openCustomerDrawer(missingOrgJobs[0].id)}
                    >
                      {t('schedule.addCustomer')}
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => navigate(`/jobs?date=${scheduleDate}`)}
                    >
                      {t('schedule.openInJobs')}
                    </Button>
                  </Space>
                }
              />
            )}

            {plan.unassignedJobs.length > 0 && (
              <Card
                id="schedule-unassigned"
                className="admin-inner-card"
                title={t('schedule.unassignedCardTitle')}
              >
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t('schedule.unassignedHelp')}
                  description={t('schedule.unassignedTimelineHint')}
                />
                <List
                  size="small"
                  dataSource={plan.unassignedJobs}
                  renderItem={(job) => {
                    const catalog = jobsById.get(job.id);
                    const pax = catalog?.passengerCount ?? 0;
                    const legCount = catalog?.legs.length ?? 0;
                    const first = catalog?.legs[0];
                    const last = catalog?.legs[legCount - 1];
                    const active = assignJobId === job.id;
                    return (
                      <List.Item
                        className={active ? 'admin-unassigned-item--active' : undefined}
                        style={{ cursor: 'pointer' }}
                        actions={[
                          <Select
                            key="sel"
                            placeholder={t('schedule.vehiclePlaceholder')}
                            style={{ width: 280 }}
                            options={vehicleSuggestionOptions(plan, job.id, t)}
                            value={vehiclePick[job.id]}
                            getPopupContainer={(node) => node.parentElement ?? document.body}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(v) =>
                              setVehiclePick((prev) => ({ ...prev, [job.id]: v }))
                            }
                            optionFilterProp="label"
                            showSearch
                            aria-label={t('schedule.assignVehicleFor', { job: job.title })}
                          />,
                          <Button
                            key="assign"
                            type="primary"
                            disabled={!canGenerate}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssign(job.id);
                            }}
                          >
                            {t('schedule.assign')}
                          </Button>,
                        ]}
                      >
                        <Space
                          direction="vertical"
                          size={0}
                          style={{ width: '100%' }}
                          onClick={() => {
                            setAssignJobId(job.id);
                            setHighlightJobId(null);
                            scrollToSection('schedule-timeline');
                          }}
                        >
                          <Text strong>{job.title}</Text>
                          <Text type="secondary">
                            {t('schedule.paxLegs', { pax, legs: legCount })}
                            {first ? ` · ${minutesLabel(first.earliestStartMinutes)}` : ''}
                            {last && last !== first
                              ? `–${minutesLabel(last.earliestStartMinutes)}`
                              : ''}
                          </Text>
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </Card>
            )}

            {publishedSplits.length > 0 && (
              <Alert
                type="warning"
                showIcon
                role="alert"
                message={t('schedule.publishedSplitTitle')}
                description={
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text>
                      {t('schedule.publishedSplitDescription')}
                    </Text>
                    {publishedSplits.map((dup) => (
                      <div key={dup.jobId}>
                        <Text strong>{dup.title}</Text>
                        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                          {dup.vehicles.map((v) => (
                            <li key={v.vehicleId}>
                              <Text>
                                {t('schedule.bus')} <Text code>{v.fleetNumber}</Text>
                                {v.legSummaries.length > 0
                                  ? ` — ${v.legSummaries.join('; ')}`
                                  : ''}
                              </Text>
                            </li>
                          ))}
                        </ul>
                        <Button
                          type="link"
                          size="small"
                          style={{ paddingLeft: 0 }}
                          onClick={() => navigate(tripsListPath)}
                        >
                          {t('schedule.reviewTripsForDate')}
                        </Button>
                      </div>
                    ))}
                  </Space>
                }
              />
            )}

            {plannerDuplicates.length > 0 && (
              <Alert
                id="schedule-duplicates"
                type="error"
                showIcon
                role="alert"
                message={t('schedule.duplicateDraftTitle')}
                description={
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text>
                      {t('schedule.duplicateDraftDescription')}
                    </Text>
                    {plannerDuplicates.map((dup) => (
                      <div key={dup.jobId}>
                        <Text strong>{dup.title}</Text>
                        <div style={{ marginTop: 8 }}>
                          <Space wrap>
                            {dup.vehicles.map((v) => (
                              <Button
                                key={v.vehicleId}
                                type="primary"
                                size="small"
                                onClick={() => {
                                  if (!plan) return;
                                  const result = assignJobToVehicle(
                                    plan,
                                    dup.jobId,
                                    v.vehicleId
                                  );
                                  if (!result.ok) {
                                    message.error(
                                      translateScheduleIssues(result.issues, t) ||
                                        result.errors.join('; ')
                                    );
                                    return;
                                  }
                                  applyPlan(result.plan);
                                  message.success(
                                    t('schedule.keptOnVehicle', {
                                      job: dup.title,
                                      vehicle: v.fleetNumber,
                                    })
                                  );
                                }}
                              >
                                {t('schedule.keepOnVehicle', { vehicle: v.fleetNumber })}
                              </Button>
                            ))}
                            <Button
                              size="small"
                              danger
                              onClick={() => {
                                if (!plan) return;
                                applyPlan(unassignJobFromPlan(plan, dup.jobId));
                                message.info(t('schedule.movedBackToUnassigned', { job: dup.title }));
                              }}
                            >
                              {t('schedule.unassignEntirely')}
                            </Button>
                          </Space>
                        </div>
                      </div>
                    ))}
                  </Space>
                }
              />
            )}

            <Card
              id="schedule-timeline"
              className="admin-inner-card"
              title={t('schedule.vehicleTimelineTitle', { date: formatDate(scheduleDate) })}
            >
              <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
                <Text type="secondary">{t('schedule.driverCrewHint')}</Text>
                {plan.vehicles.map((v) => {
                  const fleet = v.fleetNumber ?? t('schedule.bus');
                  const operatorId = operatorByVehicle.get(v.vehicleId) ?? null;
                  // Same fleet_operator_id as vehicle only; no vehicle operator → no drivers.
                  const driverOptions =
                    operatorId == null
                      ? []
                      : fleetDrivers
                          .filter((d) => d.fleet_operator_id === operatorId)
                          .map((d) => ({ value: d.id, label: d.name }));
                  return (
                    <Space key={`driver-${v.vehicleId}`} wrap align="center">
                      <Text strong style={{ minWidth: 72 }}>
                        {fleet}
                      </Text>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('schedule.driverOptional')}
                        style={{ minWidth: 220 }}
                        aria-label={t('schedule.driverSelectLabel', { fleet })}
                        value={v.driverId ?? undefined}
                        options={driverOptions}
                        onChange={(driverId) => {
                          setPlan((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  vehicles: prev.vehicles.map((row) =>
                                    row.vehicleId === v.vehicleId
                                      ? { ...row, driverId: driverId ?? null }
                                      : row
                                  ),
                                }
                              : prev
                          );
                        }}
                      />
                    </Space>
                  );
                })}
              </Space>
              <VehicleScheduleTimeline
                plan={plan}
                jobsById={jobsById}
                scheduleDate={scheduleDate}
                emphasizeVehicleIds={emphasizeVehicleIds}
                assignModeJobId={assignJobId}
                assignModeJobTitle={
                  assignJobId
                    ? plan.unassignedJobs.find((j) => j.id === assignJobId)?.title ??
                      jobsById.get(assignJobId)?.title
                    : undefined
                }
                onAssignVehicle={(vehicleId) => {
                  if (assignJobId) applyAssignToVehicle(assignJobId, vehicleId);
                }}
                highlightJobId={highlightJobId}
                onSelectJob={(jobId) => {
                  setHighlightJobId(jobId);
                  setAssignJobId(null);
                  const onInfeasible = plan.vehicles.some(
                    (v) => !v.validation.ok && v.legs.some((l) => l.jobId === jobId)
                  );
                  if (onInfeasible) {
                    scrollToSection('schedule-fix-panel');
                  } else {
                    openAssignmentsAndScroll();
                  }
                }}
                flashVehicleId={flashVehicleId}
              />
            </Card>

            <div id="schedule-assignments" className="admin-inner-card" style={{ marginBottom: 16 }}>
              {fixReport.blocking.length > 0 && (
                <Alert
                  type="error"
                  style={{ marginBottom: 12 }}
                  role="alert"
                  message={t('schedule.someVehiclesInfeasible')}
                  description={fixReport.blocking
                    .map(
                      (v) =>
                        `${v.fleetNumber ?? t('schedule.bus')}: ${
                          translateScheduleIssues(v.issues, t) || v.errors.join('; ')
                        }`
                    )
                    .join(' · ')}
                />
              )}
              {fixReport.blocking.length === 0 && fixReport.publishedWarnings.length > 0 && (
                <Alert
                  type="warning"
                  style={{ marginBottom: 12 }}
                  role="status"
                  message={t('schedule.publishedWarnTitle', {
                    count: fixReport.publishedWarnings.length,
                  })}
                  description={t('schedule.publishedWarnHint')}
                />
              )}
              <Collapse
                size="small"
                activeKey={assignmentsOpenKeys}
                onChange={(keys) =>
                  setAssignmentsOpenKeys(Array.isArray(keys) ? keys : keys ? [keys] : [])
                }
                items={[
                  {
                    key: 'assignments',
                    label: t('schedule.assignmentsTitle'),
                    children: (
              <List
                size="small"
                dataSource={plan.vehicles.flatMap((v) =>
                  jobIdsOnVehicle(plan, v.vehicleId)
                    .filter((jobId) => !isFixedJobOnVehicle(plan, v.vehicleId, jobId))
                    .map((jobId) => ({
                    vehicleId: v.vehicleId,
                    fleetNumber: v.fleetNumber,
                    jobId,
                    feasible: v.validation.ok,
                    validationErrors: v.validation.errors,
                    timeline: v.validation.timeline.filter((t) => t.jobId === jobId),
                    legLabel:
                      v.legs.find((l) => l.jobId === jobId)?.label ??
                      jobsById.get(jobId)?.title ??
                      t('schedule.untitledJob'),
                  }))
                )}
                locale={{ emptyText: t('schedule.noJobsOnVehicles') }}
                renderItem={(item) => {
                  const pinned = Boolean(plan.pins?.[item.jobId]);
                  const highlighted = highlightJobId === item.jobId;
                  const jobExpired = item.timeline.some((entry) =>
                    isPastSchedulePickup(scheduleDate, entry.arriveMinutes)
                  );
                  return (
                  <List.Item
                    className={highlighted ? 'admin-unassigned-item--active' : undefined}
                    actions={[
                            <Button
                              key="pin"
                              size="small"
                              type={pinned ? 'primary' : 'default'}
                              icon={pinned ? <PushpinFilled /> : <PushpinOutlined />}
                              aria-label={
                                pinned
                                  ? t('schedule.unpinLabel', { job: item.legLabel })
                                  : t('schedule.pinLabel', { job: item.legLabel })
                              }
                              onClick={() => {
                                applyPlan(
                                  pinned
                                    ? unpinJobOnPlan(plan, item.jobId)
                                    : pinJobOnPlan(plan, item.jobId)
                                );
                                message.success(
                                  pinned
                                    ? t('schedule.unpinned', { job: item.legLabel })
                                    : t('schedule.pinned', { job: item.legLabel })
                                );
                              }}
                            >
                              {pinned ? t('schedule.unpin') : t('schedule.pin')}
                            </Button>,
                            <Select
                              key="re"
                              placeholder={t('schedule.reassignToPlaceholder')}
                              style={{ width: 280 }}
                              options={vehicleSuggestionOptions(
                                plan,
                                item.jobId,
                                t,
                                item.vehicleId
                              )}
                              value={vehiclePick[`re:${item.jobId}`]}
                              onChange={(val) =>
                                setVehiclePick((prev) => ({
                                  ...prev,
                                  [`re:${item.jobId}`]: val,
                                }))
                              }
                              optionFilterProp="label"
                              showSearch
                              allowClear
                              aria-label={t('schedule.reassignLabel', { job: item.legLabel })}
                            />,
                            <Button
                              key="go"
                              size="small"
                              onClick={() => handleReassign(item.jobId)}
                            >
                              {t('schedule.reassign')}
                            </Button>,
                            <Button
                              key="rm"
                              size="small"
                              danger
                              onClick={() => handleUnassign(item.jobId)}
                            >
                              {t('schedule.remove')}
                            </Button>,
                          ]}
                  >
                    <Space direction="vertical" size={0}>
                      <Space size={8} wrap>
                        <Text type="secondary">{item.fleetNumber ?? t('schedule.bus')}</Text>
                        <Text strong>{item.legLabel}</Text>
                        {pinned && <Tag color="processing">{t('schedule.pinnedTag')}</Tag>}
                        {jobExpired && <Tag color="default">{t('schedule.expired')}</Tag>}
                      </Space>
                      {item.timeline.map((entry) => {
                        const legExpired = isPastSchedulePickup(
                          scheduleDate,
                          entry.arriveMinutes
                        );
                        return (
                          <Text key={entry.legId} type="secondary" style={{ fontSize: 12 }}>
                            {entry.label ?? t('schedule.leg')}: {minutesLabel(entry.arriveMinutes)}–
                            {minutesLabel(entry.departMinutes)}
                            {legExpired ? ` · ${t('schedule.expired')}` : ''}
                          </Text>
                        );
                      })}
                    </Space>
                  </List.Item>
                  );
                }}
              />
                    ),
                  },
                ]}
              />
            </div>

            <Collapse
              size="small"
              items={[
                {
                  key: 'detail',
                  label: t('schedule.timelineDetailTitle'),
                  children: (
                    <Table
                      size="small"
                      columns={columns}
                      dataSource={legRows}
                      pagination={false}
                      scroll={{ x: 700 }}
                    />
                  ),
                },
              ]}
            />

            <div
              id="schedule-publish"
              className="admin-sticky-actions"
              role="region"
              aria-label={t('schedule.publishActionsLabel')}
            >
              <div className="admin-sticky-actions__status">
                <Text strong>{formatDate(scheduleDate)}</Text>
                <Text type="secondary">{stickyStatus}</Text>
              </div>
              <Space wrap>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={generatePlan}
                  loading={loading}
                  disabled={!canGenerate}
                >
                  {t('schedule.regenerate')}
                </Button>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={!canPublish || !canPublishPlan}
                  loading={publishing}
                  onClick={confirmPublish}
                >
                  {t('schedule.confirmPublish')}
                </Button>
              </Space>
            </div>
          </>
        )}
      </div>

      <Drawer
        title={t('schedule.addCustomerTitle')}
        open={customerDrawerJobId != null}
        onClose={closeCustomerDrawer}
        destroyOnClose
        width={400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={closeCustomerDrawer}>{t('common.cancel')}</Button>
            <Button type="primary" loading={savingCustomer} onClick={() => void saveCustomerOnPlan()}>
              {t('schedule.addCustomerSave')}
            </Button>
          </Space>
        }
      >
        {customerDrawerJobId && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">{t('schedule.addCustomerJobLabel')}</Text>
              <div>
                <Text strong>
                  {missingOrgJobs.find((j) => j.id === customerDrawerJobId)?.title ??
                    plan?.jobs?.find((j) => j.id === customerDrawerJobId)?.title ??
                    customerDrawerJobId.slice(0, 8)}
                </Text>
              </div>
            </div>
            <div>
              <Text type="secondary">{t('schedule.addCustomerSelect')}</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('schedule.addCustomerSelect')}
                options={orgOptions.map((o) => ({ value: o.id, label: o.name }))}
                value={customerOrgId}
                onChange={setCustomerOrgId}
                showSearch
                optionFilterProp="label"
                aria-label={t('schedule.addCustomerSelect')}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </AdminSectionPage>
  );
}

export default DailySchedule;
